/**
 * @file     LeadCaptureContext.tsx
 * @purpose  React context that lets any descendant open the shared listing
 *           sheet (question, PDF or documents) without prop drilling.
 *           Provider mounts once at the page level (e.g. ListingDetailView);
 *           consumers call `useLeadCapture().open(payload)`.
 */
'use client';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useViewedListings } from '@/hooks/useViewedListings';

export type LeadCaptureMode = 'inquiry' | 'pdf' | 'documents';

export type RequestedDocument = { id: string; label: string };

export type ListingContext = {
  listingSlug: string;
  listingSource: 'curated' | 'mls';
  listingTitle: string;
  listingPrice: number | null;
  listingMlsNumber: string | null;
  /** Sheet header only: neighbourhood or city after the price. */
  listingPlace?: string | null;
  /** Sheet header only: the first photo. */
  listingThumb?: string | null;
};

export type OpenPayload = {
  mode: LeadCaptureMode;
  listing: ListingContext;
  /** Documents: the ones ticked when the sheet opens. */
  requestedDocuments?: RequestedDocument[];
  /** Documents: the checklist the sheet shows. Defaults to requestedDocuments. */
  documentOptions?: RequestedDocument[];
  analyticsSurface?: 'slug_page' | 'map_sheet' | 'quick_view';
  /** Fires once when the request lands. Documents requests pass the REALTOR® answer. */
  onSubmitted?: (workingWithRealtor?: boolean) => void;
  /** Sent screen: See similar homes. */
  onSeeSimilar?: () => void;
};

type LeadCaptureContextValue = {
  isOpen: boolean;
  payload: OpenPayload | null;
  open: (payload: OpenPayload) => void;
  close: () => void;
};

const LeadCaptureContext = createContext<LeadCaptureContextValue | null>(null);

export function LeadCaptureProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<OpenPayload | null>(null);
  const { record } = useViewedListings();

  const open = useCallback(
    (p: OpenPayload) => {
      record(p.listing.listingSlug);
      setPayload(p);
    },
    [record],
  );
  const close = useCallback(() => setPayload(null), []);

  const value = useMemo<LeadCaptureContextValue>(
    () => ({ isOpen: payload != null, payload, open, close }),
    [payload, open, close],
  );

  return (
    <LeadCaptureContext.Provider value={value}>{children}</LeadCaptureContext.Provider>
  );
}

export function useLeadCapture(): LeadCaptureContextValue {
  const ctx = useContext(LeadCaptureContext);
  if (!ctx) {
    throw new Error(
      'useLeadCapture must be called inside a <LeadCaptureProvider>. Mount the provider at the page root.',
    );
  }
  return ctx;
}
